import React, { useState } from "react";
import ProductTemplateColumnsDropdown, {
  Attribute,
} from "components/basic/dropdowns/product-template-columns-dropdown/ProductTemplateColumnsDropdown";
import { Column, ColumnEditorOptions } from "primereact/column";
import { DataTable, DataTableRowEditCompleteEvent } from "primereact/datatable";
import { InputSwitch } from "primereact/inputswitch";
import { InputText } from "primereact/inputtext";
import { MultiSelectChangeEvent } from "primereact/multiselect";
import { CellType } from "utils/enums";
import { InputNumber } from "primereact/inputnumber";
import { T } from "services/lang/LanguageService";
import { IProductTemplateTableEditor } from "components/ComponentTypeTypes";
import { DropdownChangeEvent } from "primereact/dropdown";

interface ProductTemplateOverriddenColumnValuesProps {
  currentConfig: IProductTemplateTableEditor;
  setCurrentConfig: React.Dispatch<React.SetStateAction<IProductTemplateTableEditor>>;
}

const allowedTypes = [CellType.STRING, CellType.DOUBLE, CellType.LONG];

const ProductTemplateOverriddenColumnValues = ({
  currentConfig,
  setCurrentConfig,
}: ProductTemplateOverriddenColumnValuesProps) => {
  const [currentSelections, setCurrentSelections] = useState<Attribute[]>();
  const overrideConfigHandler = () => {
    setCurrentConfig(prev => ({
      ...prev,
      overriddenColumnValues: prev.overriddenColumnValues
        ? {
            ...prev.overriddenColumnValues,
            overrideColumnValues: !prev.overriddenColumnValues.overrideColumnValues,
          }
        : { selectedColumnsToOverride: [], overrideColumnValues: true },
    }));
  };

  const handleRowEditComplete = (event: DataTableRowEditCompleteEvent) => {
    const { newData } = event;
    setCurrentConfig(prev => ({
      ...prev,
      overriddenColumnValues: {
        ...prev.overriddenColumnValues,
        overrideColumnValues: prev.overriddenColumnValues?.overrideColumnValues ?? true,
        selectedColumnsToOverride:
          prev.overriddenColumnValues?.selectedColumnsToOverride.map(column =>
            column.columnTitle === newData.columnTitle
              ? { ...column, defaultValue: newData.defaultValue }
              : column
          ) ?? [],
      },
    }));
  };

  const handleColumnSelection = (e: MultiSelectChangeEvent | DropdownChangeEvent) => {
    setCurrentConfig(prev => {
      const existingColumns = prev.overriddenColumnValues?.selectedColumnsToOverride ?? [];

      const columnMap = new Map(existingColumns.map(col => [col.columnTitle, col]));

      const updatedColumns = e.value.map((columnTitle: string) => {
        return columnMap.has(columnTitle) ? columnMap.get(columnTitle)! : { columnTitle, defaultValue: "" };
      });

      return {
        ...prev,
        overriddenColumnValues: {
          ...prev.overriddenColumnValues,
          overrideColumnValues: prev.overriddenColumnValues?.overrideColumnValues ?? true,
          selectedColumnsToOverride: updatedColumns,
        },
      };
    });
  };

  const inputEditorTemplate = (options: ColumnEditorOptions) => {
    const currentType = currentSelections?.find(
      selection => selection.value === options.rowData.columnTitle
    )?.type;

    if (currentType === CellType.STRING) {
      return <InputText value={options.value} onChange={e => options.editorCallback?.(e.target.value)} />;
    }
    return (
      <InputNumber value={options.value} onChange={e => options.editorCallback?.(e.value)} mode="decimal" />
    );
  };

  return (
    <div className="flex flex-column gap-3">
      <div className="gap-2 flex flex-column">
        <label htmlFor="overrideColumnValues">{T("label.override-column-values")}</label>
        <InputSwitch
          id="overrideColumnValues"
          checked={currentConfig.overriddenColumnValues?.overrideColumnValues ?? false}
          onChange={overrideConfigHandler}
        />
      </div>

      <div className="flex flex-column gap-2">
        <label htmlFor="selectColumns">{T("action.select-columns")}</label>
        <ProductTemplateColumnsDropdown
          id="selectedColumns"
          value={
            currentConfig.overriddenColumnValues?.selectedColumnsToOverride?.map(
              column => column.columnTitle
            ) ?? []
          }
          className="w-full"
          onChange={handleColumnSelection}
          selectedTemplate={{
            refName: currentConfig.safeName,
            value: currentConfig.id!,
          }}
          setSelections={setCurrentSelections}
          allowedTypes={allowedTypes}
        />
      </div>
      {(currentConfig.overriddenColumnValues?.selectedColumnsToOverride ?? []).length > 0 && (
        <DataTable
          value={currentConfig.overriddenColumnValues?.selectedColumnsToOverride ?? []}
          className="w-full"
          size="small"
          editMode="row"
          onRowEditComplete={handleRowEditComplete}
        >
          <Column field="columnTitle" header={T("word.column-name")} style={{ width: "45%" }} />
          <Column
            field="defaultValue"
            header={T("label.default-value")}
            style={{ width: "45%" }}
            editor={inputEditorTemplate}
          />
          <Column rowEditor style={{ width: "10%", textAlign: "end" }} />
        </DataTable>
      )}
    </div>
  );
};

export default ProductTemplateOverriddenColumnValues;
